import React from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { C } from "../../common/theme";
import { PayoutKeyError, decryptAccount, importPrivateKey } from "../../common/payoutKey/crypto";
import { useIsNarrow } from "../../ui/hooks";

/** Lock again after this long without a click or key press. */
export const IDLE_LOCK_MS = 15 * 60 * 1000;

interface PayoutKeyState {
  key: CryptoKey | null;
  /** Open the unlock dialog; `sample` is a ciphertext the key must open */
  requestUnlock: (sample?: string) => void;
  lock: () => void;
}

const PayoutKeyContext = React.createContext<PayoutKeyState>({ key: null, requestUnlock: () => {}, lock: () => {} });

export const usePayoutKey = () => React.useContext(PayoutKeyContext);

/** Holds the owner's payout key for this tab only: in memory, never stored
 * or sent, cleared on Lock, after 15 idle minutes, or when the tab closes. */
export function PayoutKeyProvider({ children }: { children: React.ReactNode }) {
  const [key, setKey] = React.useState<CryptoKey | null>(null);
  const [open, setOpen] = React.useState(false);
  const [sample, setSample] = React.useState<string | undefined>();
  const [pem, setPem] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const narrow = useIsNarrow(600);

  const lock = React.useCallback(() => setKey(null), []);

  React.useEffect(() => {
    if (!key) return;
    let timer = window.setTimeout(lock, IDLE_LOCK_MS);
    const bump = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(lock, IDLE_LOCK_MS);
    };
    window.addEventListener("pointerdown", bump);
    window.addEventListener("keydown", bump);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("pointerdown", bump);
      window.removeEventListener("keydown", bump);
    };
  }, [key, lock]);

  const requestUnlock = React.useCallback((s?: string) => {
    setSample(s);
    setPem("");
    setError(null);
    setOpen(true);
  }, []);

  const unlock = async () => {
    setBusy(true);
    setError(null);
    try {
      const imported = await importPrivateKey(pem);
      if (sample) await decryptAccount(imported, sample);
      setKey(imported);
      setOpen(false);
      setPem("");
    } catch (err) {
      setError(err instanceof PayoutKeyError ? err.message : "Couldn't unlock with that key.");
    } finally {
      setBusy(false);
    }
  };

  const readFile = async (file?: File) => {
    if (!file) return;
    setPem((await file.text()).trim());
    setError(null);
  };

  const value = React.useMemo(() => ({ key, requestUnlock, lock }), [key, requestUnlock, lock]);

  return (
    <PayoutKeyContext.Provider value={value}>
      {children}
      <Dialog open={open} onClose={busy ? undefined : () => setOpen(false)} fullWidth maxWidth="sm" fullScreen={narrow}>
        <DialogTitle>Unlock bank details</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography variant="body2" sx={{ color: C.ink2 }}>
            Paste your payout key. It stays in this browser tab, is never sent to our servers, and locks again after 15 minutes idle.
          </Typography>
          <TextField
            label="Payout key"
            value={pem}
            onChange={(e) => {
              setPem(e.target.value);
              setError(null);
            }}
            placeholder={"-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----"}
            multiline
            minRows={5}
            maxRows={10}
            fullWidth
            autoFocus
            inputProps={{ spellCheck: false, autoComplete: "off", style: { fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12.5 } }}
          />
          <Box>
            <Button component="label" variant="outlined" size="small" startIcon={<UploadFileOutlinedIcon />} sx={{ minHeight: 40 }}>
              Choose key file
              <input hidden type="file" accept=".pem,.key,.txt,text/plain" onChange={(e) => readFile(e.target.files?.[0])} />
            </Button>
          </Box>
          <Alert severity="info">Lose this key and sellers have to enter their details again. Keep it in your password manager.</Alert>
          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={unlock} disabled={busy || !pem.trim()}>
            {busy ? "Unlocking…" : "Unlock"}
          </Button>
        </DialogActions>
      </Dialog>
    </PayoutKeyContext.Provider>
  );
}
